import type { LocalAiProfile, Rect } from "@aimosaic/core";

export interface VlmTilePlan {
  id: string;
  bbox: Rect;
}

interface VlmTileOptions {
  width: number;
  height: number;
  profile: LocalAiProfile;
}

export function planVlmTiles({ width, height, profile }: VlmTileOptions): VlmTilePlan[] {
  const sourceWidth = Math.max(1, Math.round(width));
  const sourceHeight = Math.max(1, Math.round(height));
  const full: VlmTilePlan = {
    id: "full",
    bbox: { x: 0, y: 0, width: sourceWidth, height: sourceHeight }
  };

  if (profile === "fast") {
    return [full];
  }

  if (sourceWidth / sourceHeight >= 1.25 && sourceWidth <= 2600) {
    const yOverlap = Math.round(sourceHeight * 0.08);
    const xOverlap = Math.round(sourceWidth * 0.08);
    const middleY = Math.round(sourceHeight / 2);
    const middleX = Math.round(sourceWidth / 2);
    const bottomY = Math.max(0, middleY - yOverlap);
    return [
      full,
      {
        id: "tile-top",
        bbox: { x: 0, y: 0, width: sourceWidth, height: Math.min(sourceHeight, middleY + yOverlap) }
      },
      {
        id: "tile-bottom-left",
        bbox: {
          x: 0,
          y: bottomY,
          width: Math.min(sourceWidth, middleX + xOverlap),
          height: sourceHeight - bottomY
        }
      },
      {
        id: "tile-bottom-right",
        bbox: {
          x: Math.max(0, middleX - xOverlap),
          y: bottomY,
          width: sourceWidth - Math.max(0, middleX - xOverlap),
          height: sourceHeight - bottomY
        }
      }
    ];
  }

  const columns = profile === "precise" ? Math.max(2, Math.ceil(sourceWidth / 1200)) : 2;
  const rows = profile === "precise" ? Math.max(2, Math.ceil(sourceHeight / 1000)) : 2;
  const maxExtraTiles = profile === "precise" ? 12 : 4;
  const overlap = profile === "precise" ? 0.18 : 0;
  const tiles: VlmTilePlan[] = [full];

  for (let row = 0; row < rows && tiles.length <= maxExtraTiles; row += 1) {
    for (let column = 0; column < columns && tiles.length <= maxExtraTiles; column += 1) {
      const baseX = (sourceWidth * column) / columns;
      const baseY = (sourceHeight * row) / rows;
      const baseWidth = sourceWidth / columns;
      const baseHeight = sourceHeight / rows;
      const padX = baseWidth * overlap;
      const padY = baseHeight * overlap;
      const x = Math.max(0, Math.round(baseX - padX));
      const y = Math.max(0, Math.round(baseY - padY));
      const right = Math.min(sourceWidth, Math.round(baseX + baseWidth + padX));
      const bottom = Math.min(sourceHeight, Math.round(baseY + baseHeight + padY));
      tiles.push({
        id: `tile-${tiles.length - 1}`,
        bbox: { x, y, width: Math.max(1, right - x), height: Math.max(1, bottom - y) }
      });
    }
  }

  return tiles;
}
